import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";
import SinConexionKawaii from "../SinConexionKawaii";
import { useMapFullscreen } from "../../context/MapFullscreenContext";

// Template114: Westminster Mapa y Buckingham
// Adaptado del componente Page9.jsx de guia-londres
const Template114 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [loaded, setLoaded] = useState(false);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(true);
	const mapFullscreenContext = useMapFullscreen();
	const setMapFullscreen = mapFullscreenContext?.setMapFullscreen;

	const openFullscreen = () => {
		setFullscreen(true);
		if (typeof setMapFullscreen === "function") setMapFullscreen(true);
	};
	const closeFullscreen = () => {
		setFullscreen(false);
		if (typeof setMapFullscreen === "function") setMapFullscreen(false);
	};

	useEffect(() => {
		return () => {
			if (typeof setMapFullscreen === "function") setMapFullscreen(false);
		};
	}, [setMapFullscreen]);

	useEffect(() => {
		if (typeof navigator !== "undefined") {
			setIsOnline(navigator.onLine);
			setLoaded(navigator.onLine);
		}

		const handleOnline = () => {
			setIsOnline(true);
			setLoaded(true);
		};
		const handleOffline = () => {
			setIsOnline(false);
			setLoaded(true);
		};

		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);

		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	const headerText = page.texts?.[0]?.content;
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const mapaOffline = getImageSrc("mapa-offline");
	const mapUrl = page.maps?.[0];
	const ui = page?.ui ?? page?.data?.ui ?? page?.data?.data?.ui ?? {};
	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const stJamesPark = sections[0];
	const buckingham = sections[1];
	const theMall = sections[2];

	const jamesParkImage = getImageSrc("james-park");
	const buckinghamImage = getImageSrc("buckingham");
	const theMallImage = getImageSrc("the-mall");
	const cambioGuardiaSticker = getCanvasImageUrl("cambio-guardia");

	let actualHeaderColor = headerBackgroundColor || "#de5624";

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const contentColor = textColor || "#444";

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		color: contentColor,
		margin: 0,
	};

	if (fullscreen) {
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: actualHeaderColor,
					}}
				>
					<Box
						component="button"
						onClick={closeFullscreen}
						sx={{
							backgroundColor: "#fff",
							color: actualHeaderColor,
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						}}
					>
						{ui.backButton || "← Volver a la guía"}
					</Box>
				</Box>
				<iframe
					title={ui.mapTitleFullscreen || "Mapa grande"}
					src={mapUrl}
					width="100%"
					height="100%"
					style={{ border: 0, flex: 1 }}
					allowFullScreen
					loading="lazy"
				></iframe>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
					fontSize: "14px",
					fontWeight: 600,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 99999,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			{/* MAPA */}
			<Box data-block-id="map" sx={{ flex: 1, marginTop: "-70px", position: "relative" }}>
				{!loaded && isOnline && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							height: "100%",
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 1,
							marginTop: "30px",
						}}
					>
						<Box
							sx={{
								width: 20,
								height: 20,
								border: "4px solid #ccc",
								borderTop: `4px solid ${actualHeaderColor}`,
								borderRadius: "50%",
								animation: "spin 1s linear infinite",
								"@keyframes spin": {
									"0%": { transform: "rotate(0deg)" },
									"100%": { transform: "rotate(360deg)" },
								},
							}}
						/>
					</Box>
				)}

				{isOnline && mapUrl ? (
					<iframe
						title={ui.mapTitleEmbed || "Mapa Londres"}
						src={mapUrl}
						width="100%"
						height={isSmallScreen ? "400px" : "320px"}
						allowFullScreen=""
						loading="lazy"
						style={{ border: 0 }}
						onLoad={() => setLoaded(true)}
					></iframe>
				) : mapaOffline ? (
					<img
						src={mapaOffline}
						alt=""
						style={{
							width: "100%",
							height: isSmallScreen ? "400px" : "340px",
							objectFit: "cover",
							position: "relative",
							zIndex: 2,
						}}
					/>
				) : !isOnline ? (
					<SinConexionKawaii height={"100%"} fontFamily={fontFamily} />
				) : null}

				{isOnline && (
					<Box
						component="button"
						onClick={openFullscreen}
						sx={{
							position: "absolute",
							top: 80,
							right: 10,
							backgroundColor: actualHeaderColor,
							color: "#fff!important",
							padding: "8px 10px",
							fontSize: "11px",
							borderRadius: "8px",
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
							boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",

							border: "none",
							cursor: "pointer",
						}}
					>
						{ui.fullscreenButton || "Ver mapa grande"}
					</Box>
				)}

				{ui.tooltipGaleria && (
					<>
						<Box
							sx={{
								position: "absolute",
								bottom: 70,
								right: 5,
								backgroundColor: "#fff8db",
								border: "2px solid #f5c400",
								borderRadius: "16px 16px 0px 16px",
								padding: "10px 12px",
								fontSize: 11,
								fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
								color: contentColor,
								boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
								maxWidth: 120,
								zIndex: 3,
							}}
						>
							{ui.tooltipGaleria.text}
							{ui.tooltipGaleria.url && (
								<a
									href={ui.tooltipGaleria.url}
									target="_blank"
									rel="noreferrer"
									style={{
										color: "#caa100",
										textDecoration: "underline",
										fontWeight: "bold",
									}}
								>
									{ui.tooltipGaleria.linkText || "Reserva aquí"}
								</a>
							)}
						</Box>
						{getCanvasImageUrl("galeria-rey") && (
							<img
								src={getCanvasImageUrl("galeria-rey")}
								alt="Galeria Rey"
								style={{
									width: 110,
									position: "absolute",
									bottom: -20,
									right: -10,
									zIndex: 2,
								}}
							/>
						)}
					</>
				)}
			</Box>

			<Box sx={{ padding: "10px 12px", position: "relative" }}>
				<Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
					{/* St. James's Park */}
					{(stJamesPark?.title || stJamesPark?.body) && (
						<Box data-block-id="text-1" sx={{ display: "flex", gap: "16px", position: "relative" }}>
							{jamesParkImage && (
								<img
									src={jamesParkImage}
									alt={stJamesPark?.title}
									style={{
										width: "40%",
										height: 110,
										objectFit: "cover",
										borderRadius: 12,
									}}
								/>
							)}
							{stJamesPark?.sabiasQue?.[0] && (
								<Box sx={{ position: "absolute", top: 40, left: "33%" }}>
									<SabiasQue
										icono={stJamesPark.sabiasQue[0].icono}
										texto={stJamesPark.sabiasQue[0].texto}
									/>
								</Box>
							)}
							<Box
								sx={{ width: "60%", display: "flex", flexDirection: "column" }}
							>
								<Box>
									{stJamesPark?.title && (
										<Box component="h3" sx={estiloH3}>
											{stJamesPark.title}
										</Box>
									)}
									{stJamesPark?.body && (
										<Box component="p" sx={estiloP}>
											{stJamesPark.body}
										</Box>
									)}
								</Box>
							</Box>
						</Box>
					)}

					{/* Palacio de Buckingham */}
					{(buckingham?.title || buckingham?.body) && (
						<Box data-block-id="text-2" sx={{ display: "flex", gap: "16px", position: "relative" }}>
							{buckinghamImage && (
								<img
									src={buckinghamImage}
									alt={buckingham?.title}
									style={{
										width: "40%",
										height: 110,
										objectFit: "cover",
										borderRadius: 12,
									}}
								/>
							)}
							{buckingham?.sabiasQue?.[0] && (
								<Box sx={{ position: "absolute", top: 40, left: "33%" }}>
									<SabiasQue
										icono={buckingham.sabiasQue[0].icono}
										texto={buckingham.sabiasQue[0].texto}
									/>
								</Box>
							)}
							{buckingham?.sabiasQue?.[1] && (
								<Box sx={{ position: "absolute", top: -10, left: "33%" }}>
									<SabiasQue
										icono={buckingham.sabiasQue[1].icono}
										texto={buckingham.sabiasQue[1].texto}
									/>
								</Box>
							)}
							<Box sx={{ width: "60%" }}>
								{buckingham?.title && (
									<Box component="h3" sx={estiloH3}>
										{buckingham.title}
									</Box>
								)}
								{buckingham?.body && (
									<Box component="p" sx={estiloP}>
										{buckingham.body}
									</Box>
								)}
							</Box>
						</Box>
					)}

					{/* The Mall */}
					{(theMall?.title || theMall?.body) && (
						<Box data-block-id="text-3" sx={{ display: "flex", gap: "16px", position: "relative" }}>
							{theMallImage && (
								<img
									src={theMallImage}
									alt={theMall?.title}
									style={{
										width: "40%",
										height: 110,
										objectFit: "cover",
										borderRadius: 12,
									}}
								/>
							)}
							{theMall?.sabiasQue?.[0] && (
								<Box sx={{ position: "absolute", top: 40, left: "33%" }}>
									<SabiasQue
										icono={theMall.sabiasQue[0].icono}
										texto={theMall.sabiasQue[0].texto}
									/>
								</Box>
							)}
							{theMall?.sabiasQue?.[1] && (
								<Box sx={{ position: "absolute", top: -10, left: "33%" }}>
									<SabiasQue
										icono={theMall.sabiasQue[1].icono}
										texto={theMall.sabiasQue[1].texto}
									/>
								</Box>
							)}
							<Box
								sx={{ width: "60%", display: "flex", flexDirection: "column" }}
							>
								<Box>
									{theMall?.title && (
										<Box component="h3" sx={estiloH3}>
											{theMall.title}
										</Box>
									)}
									{theMall?.body && (
										<Box component="p" sx={estiloP}>
											{theMall.body}
										</Box>
									)}
								</Box>
								{cambioGuardiaSticker && (
									<img
										src={cambioGuardiaSticker}
										alt=""
										style={{
											width: 100,
											position: "absolute",
											bottom: 20,
											left: isSmallScreen ? 60 : 140,
											zIndex: 2,
										}}
									/>
								)}
								{theMall?.cambioGuardia && (
									<Box
										sx={{
											backgroundColor: "#f4e4df",
											border: "3px solid #ec7447",
											borderRadius: "12px",
											padding: "12px 18px",
											paddingBottom: "0px",
											fontSize: 12,
											fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
											color: "#333",
											marginTop: "8px",
										}}
									>
										{theMall.cambioGuardia.title && (
											<Box
												component="h4"
												sx={{
													margin: 0,
													marginBottom: "0px",
													fontSize: 13,
													fontWeight: 700,
													color: "#ec7447!important",
													textTransform: "uppercase",
												}}
											>
												{theMall.cambioGuardia.title}
											</Box>
										)}
										{theMall.cambioGuardia.body && (
											<Box
												component="p"
												sx={{
													fontFamily: `${
														fontFamily || "'Poppins', sans-serif"
													}`,
												}}
											>
												{theMall.cambioGuardia.body}
											</Box>
										)}
									</Box>
								)}
							</Box>
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	);
};

Template114.thumbnail = "/images/template-1.png";

export default Template114;
